/**
 * クラフトできる物の定義（§3.4）。
 *
 * データは items.json。ここは型を付けて引きやすくするだけ。
 * 「作る」と「置く」を分けてあるのが今の設計で、
 * 作った物はインベントリに入り、置き場所は後から決める。
 */
import itemData from '../data/items.json';
import { MaterialBundle } from './materials';

export type ItemId =
  | 'box'
  | 'desk'
  | 'bookshelf'
  | 'bed'
  | 'frame'
  | 'pennant'
  | 'shelf'
  | 'lamp'
  | 'hang'
  | 'clock'
  | 'post'
  | 'tower';

/**
 * 置き方の分類。
 * platform … 1マスの足場。跳躍グラフを変える
 * build    … 複数マスの家具。足場になり、**猫が段づたいに登れる**（§10.16）
 * comfort  … 寝床・爪とぎ。通行の邪魔をしない（§3.4）
 * decor    … 壁の飾り。何にも効かない（§10.19）。支えも要らない
 */
export type ItemKind = 'platform' | 'build' | 'comfort' | 'decor';

export interface ItemDef {
  id: ItemId;
  name: string;
  kind: ItemKind;
  cost: MaterialBundle;
  /** 複数マスの物の形。アンカーからの [dc, dr] */
  shape?: readonly (readonly [number, number])[];
  /** 傷みやすさ。0 は壊れない */
  wearFactor: number;
  /** このフロアを開通させると作れるようになる。null は最初から */
  unlockedFrom: string | null;
  hint: string;
}

export const ITEMS: readonly ItemDef[] = itemData.items as unknown as ItemDef[];

const BY_ID = new Map<ItemId, ItemDef>(ITEMS.map((i) => [i.id, i]));

export function itemDef(id: ItemId): ItemDef {
  const def = BY_ID.get(id);
  if (!def) throw new Error(`unknown item: ${id}`);
  return def;
}

export function itemCost(id: ItemId): MaterialBundle {
  return itemDef(id).cost;
}

export function isSolidItem(id: ItemId): boolean {
  const k = itemDef(id).kind;
  return k === 'platform' || k === 'build';
}

/** 複数マスの物が占めるマス。左右反転して置ける */
export function itemCells(
  id: ItemId,
  c: number,
  r: number,
  flip: boolean,
): { c: number; r: number }[] {
  const shape = itemDef(id).shape ?? [[0, 0] as const];
  return shape.map(([dc, dr]) => ({ c: c + (flip ? -dc : dc), r: r + dr }));
}

/** grid の飾り層に入れる番号（0 は「無し」） */
export function itemIndex(id: ItemId): number {
  return ITEMS.findIndex((i) => i.id === id) + 1;
}

export function itemAtIndex(n: number): ItemDef | null {
  return n > 0 ? (ITEMS[n - 1] ?? null) : null;
}

/** 左右の向きが意味を持つ物か（1マスや縦積みの物は反転しても同じ） */
export function itemHasFacing(id: ItemId): boolean {
  const shape = itemDef(id).shape;
  return !!shape && shape.some(([dc]) => dc !== 0);
}
